import React, { useEffect, useState } from "react";
import { KeyRound, Power, Trash2, UserPlus } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function GeneralManagerAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ name: "عبدالرحمن فايز", username: "aaa", password: "" });
  const [loading, setLoading] = useState(true);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setAccounts((await api.get("/general-managers")).data);
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (event) => {
    event.preventDefault();
    try {
      await api.post("/general-managers", form);
      toast.success("تم إنشاء حساب المدير العام بصلاحية الاطلاع فقط");
      setForm({ name: "", username: "", password: "" });
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  const toggleActive = async (account) => {
    try {
      await api.put(`/general-managers/${account.id}`, { active: !account.active });
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  const remove = async () => {
    if (!accountToDelete) return;
    try {
      await api.delete(`/general-managers/${accountToDelete.id}`);
      toast.success("تم حذف الحساب");
      setAccountToDelete(null);
      await load();
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  return (
    <section className="space-y-4 rounded-2xl border border-[#e6e2d8] bg-white p-5">
      <div>
        <h2 className="text-xl font-bold text-[#1A201C]">حسابات المدير العام</h2>
        <p className="mt-1 text-sm text-[#4A554E]">يُمنح الحساب صلاحية الاطلاع فقط، وتقتصر رسائله الداخلية على المشرف الذي أنشأ الحساب.</p>
      </div>
      <form onSubmit={create} className="grid gap-3 md:grid-cols-4">
        <div>
          <Label className="mb-1.5 block">الاسم</Label>
          <Input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </div>
        <div>
          <Label className="mb-1.5 block">اسم المستخدم</Label>
          <Input required value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} />
        </div>
        <div>
          <Label className="mb-1.5 block">كلمة مرور مؤقتة (8 أحرف على الأقل)</Label>
          <Input required minLength={8} type="password" autoComplete="new-password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
        </div>
        <div className="flex items-end">
          <Button type="submit" className="w-full rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]"><UserPlus className="me-2 h-4 w-4" />إنشاء الحساب</Button>
        </div>
      </form>
      <div className="space-y-2">
        {loading ? <p className="text-sm text-[#4A554E]">جاري تحميل الحسابات...</p> : accounts.map((account) => (
          <div key={account.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#F9F8F6] p-3">
            <div>
              <div className="font-semibold text-[#1A201C]">{account.name}</div>
              <div className="flex items-center gap-1 text-xs text-[#4A554E]"><KeyRound className="h-3 w-3" />{account.username} · {account.active ? "نشط" : "معطل"}</div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => toggleActive(account)} title={account.active ? "تعطيل" : "تفعيل"}><Power className="h-4 w-4" /></Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setAccountToDelete(account)} className="text-[#B84B4B]" title="حذف"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
        {!loading && accounts.length === 0 && <p className="text-sm text-[#4A554E]">لم يُنشأ حساب مدير عام بعد.</p>}
      </div>
      <AlertDialog open={Boolean(accountToDelete)} onOpenChange={(open) => { if (!open) setAccountToDelete(null); }}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد حذف الحساب</AlertDialogTitle>
            <AlertDialogDescription>هل تريد حذف حساب {accountToDelete?.name}؟ لا يمكن التراجع عن هذا الإجراء.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#B84B4B] text-white hover:bg-[#963A3A]">حذف الحساب</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
